import { appAlert } from '@/lib/appAlert';
import { getErrorMessage } from '@/lib/functions';
import { formatGroupedAmount, parseGroupedAmount } from '@/lib/amountFormat';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRouter } from 'expo-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { DatePickerField } from '@/components/ui/DatePickerField';
import { Input } from '@/components/ui/Input';
import { WizardLayout } from '@/components/ui/WizardLayout';
import { contentMaxWidth, fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/contexts/ProfileContext';
import { searchProfiles, type SearchProfileResult } from '@/lib/profiles';
import { createMultiPartyAgreement } from '@/lib/multiParty';
import { fillTemplate, listAgreementTemplates, type AgreementTemplate } from '@/lib/agreementTemplates';
import { formatAmount } from '@/lib/format';
import { CURRENCIES } from '@/lib/currencies';
import { ErrorText } from '@/components/ErrorText';

type PartyRow =
  | { kind: 'user'; profile: SearchProfileResult; share: number }
  | { kind: 'email'; email: string; share: number };
type SplitMode = 'percent' | 'amount';
type ShareKey = 'creator' | string;

/** Σταθερό κλειδί ανά γραμμή — profile.id για εγγεγραμμένους, το ίδιο το email για προσκεκλημένους. */
function partyKey(p: PartyRow): string {
  return p.kind === 'user' ? p.profile.id : `email:${p.email.toLowerCase()}`;
}

function partyLabel(p: PartyRow, t: (key: string, opts?: Record<string, unknown>) => string): string {
  return p.kind === 'user' ? (p.profile.full_name || t('agreements.multi_userFallback')) : t('agreements.multi_emailInviteLabel', { email: p.email });
}

/**
 * Αναδιανομή του υπολοίπου (total - v) εξίσου στις μη-κλειδωμένες θέσεις
 * (creator + λοιποί). Οι κλειδωμένες θέσεις (εκτός της ίδιας που άλλαξε ο
 * χρήστης) μένουν αμετάβλητες — δεν επηρεάζονται από αλλαγές των άλλων.
 * Λειτουργεί είτε σε % (total=100) είτε σε € (total=ποσό).
 */
function rebalance(
  creatorShare: number,
  parties: PartyRow[],
  changedKey: ShareKey,
  newValue: number,
  total: number,
  creatorLocked: boolean,
  lockedIds: Set<string>,
): { creator: number; parties: PartyRow[] } {
  const clamped = Math.max(0, Math.min(total, newValue));

  const otherKeys: ShareKey[] = [];
  if (changedKey !== 'creator' && !creatorLocked) otherKeys.push('creator');
  parties.forEach((p) => {
    const k = partyKey(p);
    if (k !== changedKey && !lockedIds.has(k)) otherKeys.push(k);
  });

  const lockedSum =
    (creatorLocked && changedKey !== 'creator' ? creatorShare : 0) +
    parties.reduce((s, p) => { const k = partyKey(p); return s + (lockedIds.has(k) && k !== changedKey ? p.share : 0); }, 0);

  const remainder = Math.max(0, total - clamped - lockedSum);
  const per = otherKeys.length > 0 ? Math.floor((remainder / otherKeys.length) * 100) / 100 : 0;
  const leftover = Math.round((remainder - per * otherKeys.length) * 100) / 100;

  let creator = changedKey === 'creator' ? clamped : (creatorLocked ? creatorShare : per);
  const newParties = parties.map((p) => {
    const k = partyKey(p);
    if (k === changedKey) return { ...p, share: clamped };
    if (lockedIds.has(k)) return p;
    return { ...p, share: per };
  });

  // Δώσε το leftover στην τελευταία μη-κλειδωμένη θέση για ακριβές άθροισμα με το total.
  if (otherKeys.length > 0) {
    const last = otherKeys[otherKeys.length - 1];
    if (last === 'creator') {
      creator = Math.round((creator + leftover) * 100) / 100;
    } else {
      const idx = newParties.findIndex((p) => partyKey(p) === last);
      if (idx >= 0) newParties[idx] = { ...newParties[idx], share: Math.round((newParties[idx].share + leftover) * 100) / 100 };
    }
  }

  return { creator, parties: newParties };
}

/** Ισομερής κατανομή του total στις μη-κλειδωμένες θέσεις (χρησιμοποιείται σε αλλαγή συνολικού ποσού/λειτουργίας). */
function distributeEvenly(
  total: number,
  creatorShare: number,
  parties: PartyRow[],
  creatorLocked: boolean,
  lockedIds: Set<string>,
): { creator: number; parties: PartyRow[] } {
  const unlockedKeys: ShareKey[] = [];
  if (!creatorLocked) unlockedKeys.push('creator');
  parties.forEach((p) => { const k = partyKey(p); if (!lockedIds.has(k)) unlockedKeys.push(k); });

  const lockedSum = (creatorLocked ? creatorShare : 0) + parties.reduce((s, p) => s + (lockedIds.has(partyKey(p)) ? p.share : 0), 0);
  const remainder = Math.max(0, total - lockedSum);
  const per = unlockedKeys.length > 0 ? Math.floor((remainder / unlockedKeys.length) * 100) / 100 : 0;
  const leftover = Math.round((remainder - per * unlockedKeys.length) * 100) / 100;

  let creator = creatorLocked ? creatorShare : per;
  const newParties = parties.map((p) => (lockedIds.has(partyKey(p)) ? p : { ...p, share: per }));

  if (unlockedKeys.length > 0) {
    const last = unlockedKeys[unlockedKeys.length - 1];
    if (last === 'creator') {
      creator = Math.round((creator + leftover) * 100) / 100;
    } else {
      const idx = newParties.findIndex((p) => partyKey(p) === last);
      if (idx >= 0) newParties[idx] = { ...newParties[idx], share: Math.round((newParties[idx].share + leftover) * 100) / 100 };
    }
  }
  return { creator, parties: newParties };
}

export default function NewMultiPartyAgreementScreen() {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const navigation = useNavigation();
  const { user } = useAuth();
  const { profile } = useProfile();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const bypassLeaveGuardRef = useRef(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [templates, setTemplates] = useState<AgreementTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [splitMode, setSplitMode] = useState<SplitMode>('percent');
  const [currency, setCurrency] = useState('EUR');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);

  useEffect(() => {
    void listAgreementTemplates(true).then(setTemplates).catch(() => setTemplates([]));
  }, []);

  const EXPIRY_LABELS: { key: string; label: string }[] = [
    { key: 'Ημερομηνία λήξης',      label: t('agreements.expiry_due') },
    { key: 'Ημερομηνία Παράδοσης',  label: t('agreements.expiry_delivery') },
    { key: 'Ημερομηνία εξόφλησης',  label: t('agreements.expiryLabelPayment') },
  ];
  const [agreementType, setAgreementType] = useState<'one_off' | 'ongoing' | 'fixed_term'>('one_off');
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [expiryLabel, setExpiryLabel] = useState('Ημερομηνία λήξης');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const [creatorShare, setCreatorShare] = useState(34);
  const [parties, setParties] = useState<PartyRow[]>([]);
  const [creatorLocked, setCreatorLocked] = useState(false);
  const [lockedIds, setLockedIds] = useState<Set<string>>(new Set());

  function toggleCreatorLock() {
    setCreatorLocked((v) => !v);
  }

  function togglePartyLock(id: string) {
    setLockedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const splitTotal = splitMode === 'percent' ? 100 : (parseGroupedAmount(amount) ?? 0);
  const currencySymbol = CURRENCIES.find((c) => c.code === currency)?.symbol ?? currency;
  const unitSuffix = splitMode === 'percent' ? '%' : currencySymbol;

  function applyTemplate(tpl: AgreementTemplate | null) {
    setSelectedTemplateId(tpl?.id ?? null);
    if (!tpl) return;
    const partyNames = parties.map((p) => partyLabel(p, t)).filter(Boolean);
    // "Α, Β και Γ" αντί για "Α, Β, Γ" — πιο φυσικό ελληνικό στα πρότυπα 3+ ατόμων.
    const partyList = partyNames.length <= 1
      ? partyNames.join('')
      : `${partyNames.slice(0, -1).join(', ')} ${t('common.and')} ${partyNames[partyNames.length - 1]}`;
    const amountValue = amount.trim() ? Number(amount.trim().replace(',', '.')) : null;
    const amountText = amountValue != null && !Number.isNaN(amountValue)
      ? (splitMode === 'percent' ? '' : formatAmount(amountValue, currency))
      : '';
    const professionalName = profile?.full_name?.trim() || '';
    const businessName = profile?.show_business_name && profile?.business_name?.trim()
      ? profile.business_name.trim()
      : professionalName;
    setDescription(fillTemplate(tpl.template_text, {
      counterparty_name: partyNames.join(', '),
      party_list: partyList,
      amount: amountText,
      professional_name: professionalName,
      business_name: businessName,
    }));
  }

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchProfileResult[]>([]);
  const [searching, setSearching] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'form' | 'review'>('form');

  const total = creatorShare + parties.reduce((s, p) => s + p.share, 0);

  function onChangeAmount(rawText: string) {
    const text = formatGroupedAmount(rawText);
    setAmount(text);
    if (splitMode !== 'amount') return;
    const newTotal = parseGroupedAmount(text) ?? 0;
    const { creator, parties: np } = distributeEvenly(newTotal, creatorShare, parties, creatorLocked, lockedIds);
    setCreatorShare(creator);
    setParties(np);
  }

  function switchSplitMode(mode: SplitMode) {
    if (mode === splitMode) return;
    setSplitMode(mode);
    const newTotal = mode === 'percent' ? 100 : (Number(amount.trim().replace(',', '.')) || 0);
    const { creator, parties: np } = distributeEvenly(newTotal, creatorShare, parties, creatorLocked, lockedIds);
    setCreatorShare(creator);
    setParties(np);
  }

  const isEmailQuery = query.includes('@');

  async function onSearch(q: string) {
    setQuery(q);
    if (q.includes('@')) { setResults([]); return; }
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const found = await searchProfiles(q, user?.id);
      setResults(found.filter((p) => !parties.some((pp) => pp.kind === 'user' && pp.profile.id === p.id)));
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }

  function addPartyRow(row: PartyRow) {
    if (parties.length >= 6) {
      setError(t('agreements.multi_maxPartiesError'));
      return;
    }
    const key = partyKey(row);
    if (parties.some((p) => partyKey(p) === key)) {
      setError(t('agreements.multi_duplicatePartyError'));
      return;
    }
    setError(null);
    const next = [...parties, row];
    const { creator, parties: rebalanced } = rebalance(creatorShare, next, key, Math.round(splitTotal / (next.length + 1)), splitTotal, creatorLocked, lockedIds);
    setCreatorShare(creator);
    setParties(rebalanced);
    setQuery('');
    setResults([]);
  }

  function addParty(p: SearchProfileResult) {
    addPartyRow({ kind: 'user', profile: p, share: 0 });
  }

  function addPartyByEmail() {
    const email = query.trim();
    if (!email || !email.includes('@')) { setError(t('agreements.multi_invalidEmailError')); return; }
    addPartyRow({ kind: 'email', email, share: 0 });
  }

  function removeParty(index: number) {
    const removedKey = parties[index] ? partyKey(parties[index]) : null;
    const remaining = parties.filter((_, i) => i !== index);
    if (removedKey) setLockedIds((prev) => { const next = new Set(prev); next.delete(removedKey); return next; });
    if (remaining.length === 0) { setParties([]); setCreatorShare(splitTotal); return; }
    const { creator, parties: rebalanced } = rebalance(creatorShare, remaining, partyKey(remaining[0]), remaining[0].share, splitTotal, creatorLocked, lockedIds);
    setCreatorShare(creator);
    setParties(rebalanced);
  }

  /**
   * Το πεδίο που πληκτρολογεί αυτή τη στιγμή ο χρήστης δείχνει ό,τι έγραψε
   * αυτολεξεί (π.χ. "35." πριν προλάβει να γράψει το "2") αντί για το
   * στρογγυλεμένο αριθμητικό state — αλλιώς το "." κόβεται σε κάθε render
   * και δεν προλαβαίνεις ποτέ να γράψεις δεκαδικά.
   */
  const [editingKey, setEditingKey] = useState<ShareKey | null>(null);
  const [editingText, setEditingText] = useState('');

  function shareDisplayValue(key: ShareKey, numericValue: number): string {
    return editingKey === key ? editingText : String(numericValue);
  }

  function stopEditingShare() {
    setEditingKey(null);
  }

  function onChangeCreatorShare(text: string) {
    setEditingKey('creator');
    setEditingText(text);
    const v = Number(text.replace(',', '.'));
    if (!Number.isFinite(v)) return;
    const { creator, parties: rebalanced } = rebalance(creatorShare, parties, 'creator', v, splitTotal, creatorLocked, lockedIds);
    setCreatorShare(creator);
    setParties(rebalanced);
  }

  function onChangePartyShare(index: number, text: string) {
    const row = parties[index];
    if (!row) return;
    const key = partyKey(row);
    setEditingKey(key);
    setEditingText(text);
    const v = Number(text.replace(',', '.'));
    if (!Number.isFinite(v)) return;
    const { creator, parties: rebalanced } = rebalance(creatorShare, parties, key, v, splitTotal, creatorLocked, lockedIds);
    setCreatorShare(creator);
    setParties(rebalanced);
  }

  /** Επιστρέφει null αν όλα είναι έγκυρα, αλλιώς μήνυμα σφάλματος. */
  function validate(): string | null {
    if (title.trim().length < 3) return t('agreements.multi_titleTooShort');
    if (parties.length < 2) return t('agreements.multi_needTwoParties');
    if (splitMode === 'amount' && splitTotal <= 0) return t('agreements.multi_amountRequired');
    if (Math.abs(total - splitTotal) > 0.5) {
      return splitMode === 'percent'
        ? t('agreements.multi_percentMismatch', { total: total.toFixed(1) })
        : t('agreements.multi_amountMismatch', { splitTotal: splitTotal.toFixed(2), total: total.toFixed(2) });
    }
    if (agreementType === 'fixed_term') {
      if (!startDate) return t('agreements.errorStartDateRequired');
      if (!endDate) return t('agreements.errorEndDateRequired');
      if (endDate <= startDate) return t('agreements.errorEndAfterStart');
    }
    return null;
  }

  function goToReview() {
    const msg = validate();
    if (msg) { setError(msg); return; }
    setError(null);
    setStep('review');
  }

  /** Ποτέ δεν "στέλνεται" απευθείας — δημιουργείται πάντα ως πρόχειρο· η αποστολή γίνεται ρητά από την οθόνη της συμφωνίας. */
  async function onSubmit() {
    const msg = validate();
    if (msg) { setError(msg); setStep('form'); return; }
    setError(null);

    // Το RPC αποθηκεύει πάντα ποσοστά· σε λειτουργία "Ποσό" μετατρέπουμε πριν την αποστολή.
    const toPercent = (v: number) => splitMode === 'percent' ? v : Math.round((v / splitTotal) * 10000) / 100;

    setBusy(true);
    try {
      const agreementId = await createMultiPartyAgreement({
        title: title.trim(),
        description: description.trim() || null,
        amount: amount.trim() ? Number(amount.trim().replace(',', '.')) : null,
        currency,
        creatorShare: toPercent(creatorShare),
        partyIds: parties.filter((p) => p.kind === 'user').map((p) => p.profile.id),
        shares: parties.filter((p) => p.kind === 'user').map((p) => toPercent(p.share)),
        partyEmails: parties.filter((p) => p.kind === 'email').map((p) => p.email),
        emailShares: parties.filter((p) => p.kind === 'email').map((p) => toPercent(p.share)),
        includeProfessionalTerms: false,
        type: agreementType,
        expiresAt: agreementType === 'one_off' && expiryDate ? (() => { const d = new Date(expiryDate); d.setHours(23, 59, 59, 0); return d.toISOString(); })() : null,
        expiresAtLabel: agreementType === 'one_off' && expiryDate ? expiryLabel : null,
        startDate: agreementType === 'fixed_term' && startDate ? startDate.toISOString().slice(0, 10) : null,
        endDate: agreementType === 'fixed_term' && endDate ? endDate.toISOString().slice(0, 10) : null,
      });
      bypassLeaveGuardRef.current = true;
      router.replace({ pathname: '/(app)/agreements/[id]', params: { id: agreementId } });
    } catch (e) {
      setError(getErrorMessage(e, t('agreements.multi_createFailed')));
    } finally {
      setBusy(false);
    }
  }

  function hasUnsavedData(): boolean {
    return title.trim().length > 0 || parties.length > 0;
  }

  function confirmClose() {
    if (!hasUnsavedData()) { bypassLeaveGuardRef.current = true; router.back(); return; }
    const msg = validate();
    if (msg) {
      appAlert(t('agreements.multi_incompleteTitle'), t('agreements.multi_incompleteBody', { msg }), [
        { text: t('agreements.multi_continueEditing'), style: 'cancel' },
        { text: t('agreements.discard'), style: 'destructive', onPress: () => { bypassLeaveGuardRef.current = true; router.back(); } },
      ]);
      return;
    }
    appAlert(
      t('agreements.saveDraftPromptTitle'),
      t('agreements.multi_saveDraftPromptBody'),
      [
        { text: t('agreements.discard'), style: 'destructive', onPress: () => { bypassLeaveGuardRef.current = true; router.back(); } },
        { text: t('common.cancel'), style: 'cancel' },
        { text: t('common.save'), onPress: () => void onSubmit() },
      ],
    );
  }

  // Πιάνει ΚΑΘΕ τρόπο εξόδου από την οθόνη — όχι μόνο το ρητό X του WizardLayout.
  useEffect(() => {
    const unsubscribe = navigation.addListener('beforeRemove', (e) => {
      if (bypassLeaveGuardRef.current || !hasUnsavedData()) return;
      e.preventDefault();
      const msg = validate();
      if (msg) {
        appAlert(t('agreements.multi_incompleteTitle'), t('agreements.multi_incompleteBody', { msg }), [
          { text: t('agreements.multi_continueEditing'), style: 'cancel' },
          {
            text: t('agreements.discard'), style: 'destructive', onPress: () => {
              bypassLeaveGuardRef.current = true;
              navigation.dispatch(e.data.action);
            },
          },
        ]);
        return;
      }
      appAlert(
        t('agreements.saveDraftPromptTitle'),
        t('agreements.multi_saveDraftPromptBody'),
        [
          {
            text: t('agreements.discard'), style: 'destructive', onPress: () => {
              bypassLeaveGuardRef.current = true;
              navigation.dispatch(e.data.action);
            },
          },
          { text: t('common.cancel'), style: 'cancel' },
          { text: t('common.save'), onPress: () => void onSubmit() },
        ],
      );
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation, title, parties, splitMode, splitTotal, total]);

  return (
    <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView
        contentContainerStyle={[styles.content, { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.xl }]}
        keyboardShouldPersistTaps="handled"
      >
        <WizardLayout
          step={step === 'form' ? 1 : 2}
          totalSteps={2}
          title={step === 'form' ? t('agreements.multi_wizardTitle') : t('agreements.multi_wizardReviewTitle')}
          subtitle={step === 'form' ? t('agreements.multi_wizardFormSubtitle') : t('agreements.multi_wizardReviewSubtitle')}
          onBack={step === 'review' ? () => setStep('form') : undefined}
          onClose={confirmClose}
        />

        {step === 'form' && (
        <>
        <Input label={t('agreements.multi_titleFieldLabel')} value={title} onChangeText={setTitle} placeholder={t('agreements.multi_titleFieldPlaceholder')} maxLength={100} />

        {templates.length > 0 && (
          <View style={styles.templateSection}>
            <Text style={styles.templateSectionLabel}>{t('agreements.templatesLabel')}</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.templateChipRow}>
              <Pressable
                style={[styles.templateChip, selectedTemplateId === null && styles.templateChipActive]}
                onPress={() => applyTemplate(null)}
              >
                <Text style={[styles.templateChipText, selectedTemplateId === null && styles.templateChipTextActive]}>
                  {t('agreements.templateFreeText')}
                </Text>
              </Pressable>
              {templates.map((tpl) => (
                <Pressable
                  key={tpl.id}
                  style={[styles.templateChip, selectedTemplateId === tpl.id && styles.templateChipActive]}
                  onPress={() => applyTemplate(tpl)}
                >
                  <Text style={[styles.templateChipText, selectedTemplateId === tpl.id && styles.templateChipTextActive]} numberOfLines={1}>
                    {tpl.name}
                  </Text>
                </Pressable>
              ))}
            </ScrollView>
          </View>
        )}

        <Input
          label={t('agreements.multi_termsFieldLabel')}
          value={description}
          onChangeText={setDescription}
          multiline
          numberOfLines={7}
          placeholder={t('agreements.multi_termsFieldPlaceholder')}
          style={{ height: 170, paddingTop: spacing.md, textAlignVertical: 'top' }}
        />
        <Input
          label={splitMode === 'amount' ? t('agreements.multi_amountFieldLabelRequired') : t('agreements.multi_amountFieldLabelOptional')}
          value={amount}
          onChangeText={onChangeAmount}
          keyboardType="decimal-pad"
          placeholder={t('agreements.multi_amountFieldPlaceholder')}
          style={styles.amountInputLarge}
        />
        <View>
          <Pressable
            style={styles.currencyTrigger}
            onPress={() => setShowCurrencyDropdown((v) => !v)}
            accessibilityRole="button"
          >
            {(() => { const sel = CURRENCIES.find((c) => c.code === currency)!; return (
              <>
                <Text style={styles.currencyTriggerSymbol}>{sel.symbol}</Text>
                <Text style={styles.currencyTriggerLabel}>{i18n.language.startsWith('en') ? sel.en : sel.el} ({sel.code})</Text>
                <Ionicons name={showCurrencyDropdown ? 'chevron-up' : 'chevron-down'} size={14} color={colors.textMuted} />
              </>
            ); })()}
          </Pressable>
          {showCurrencyDropdown && (
            <View style={styles.currencyDropdown}>
              {CURRENCIES.map((c) => {
                const sel = currency === c.code;
                return (
                  <Pressable
                    key={c.code}
                    style={[styles.currencyOption, sel && styles.currencyOptionSelected]}
                    onPress={() => { setCurrency(c.code); setShowCurrencyDropdown(false); }}
                    accessibilityRole="button"
                  >
                    <Text style={[styles.currencyOptionSymbol, sel && styles.currencyOptionSymbolSelected]}>{c.symbol}</Text>
                    <Text style={[styles.currencyOptionLabel, sel && styles.currencyOptionLabelSelected]}>
                      {i18n.language.startsWith('en') ? c.en : c.el} <Text style={styles.currencyOptionCode}>{c.code}</Text>
                    </Text>
                    {sel && <Ionicons name="checkmark" size={14} color={colors.primary} style={{ marginLeft: 'auto' }} />}
                  </Pressable>
                );
              })}
            </View>
          )}
        </View>

        {/* Τύπος & Διάρκεια συμφωνίας */}
        <Text style={styles.sectionTitle}>{t('agreements.type_label')}</Text>
        <View style={styles.typeCards}>
          {([
            { key: 'one_off' as const, label: t('agreements.type_one_off'), icon: 'flash-outline', sub: t('agreements.type_one_off_sub') },
            { key: 'ongoing' as const, label: t('agreements.type_ongoing'), icon: 'infinite-outline', sub: t('agreements.type_ongoing_sub') },
            { key: 'fixed_term' as const, label: t('agreements.type_fixed_term'), icon: 'calendar-outline', sub: t('agreements.type_fixed_term_sub') },
          ] as const).map((opt) => {
            const sel = agreementType === opt.key;
            return (
              <Pressable
                key={opt.key}
                onPress={() => setAgreementType(opt.key)}
                style={[styles.typeCard, sel && styles.typeCardSelected]}
                accessibilityRole="button"
              >
                <Ionicons name={opt.icon} size={20} color={sel ? colors.primary : colors.textMuted} />
                <Text style={[styles.typeCardLabel, sel && styles.typeCardLabelSelected]}>{opt.label}</Text>
                <Text style={styles.typeCardSub}>{opt.sub}</Text>
              </Pressable>
            );
          })}
        </View>

        {agreementType === 'one_off' && (
          <>
            <Text style={styles.fieldLabel}>{t('agreements.milestoneDateLabel')}</Text>
            <DatePickerField value={expiryDate} onChange={setExpiryDate} minimumDate={new Date()} />
            <Text style={styles.noticeText}>
              {expiryDate ? t('agreements.milestoneDateHintSet') : t('agreements.milestoneDateHintEmpty')}
            </Text>
            {expiryDate && (
              <View style={styles.labelChips}>
                {EXPIRY_LABELS.map((opt) => {
                  const sel = expiryLabel === opt.key;
                  return (
                    <Pressable
                      key={opt.key}
                      onPress={() => setExpiryLabel(opt.key)}
                      style={[styles.labelChip, sel && styles.labelChipSelected]}
                      accessibilityRole="button"
                    >
                      <Text style={[styles.labelChipText, sel && styles.labelChipTextSelected]}>{opt.label}</Text>
                    </Pressable>
                  );
                })}
              </View>
            )}
          </>
        )}

        {agreementType === 'fixed_term' && (
          <>
            <Text style={styles.fieldLabel}>{t('agreements.startDateLabel')}</Text>
            <DatePickerField value={startDate} onChange={setStartDate} minimumDate={new Date()} />
            <Text style={styles.fieldLabel}>{t('agreements.endDateLabel')}</Text>
            <DatePickerField value={endDate} onChange={setEndDate} minimumDate={startDate ?? new Date()} />
            {startDate && endDate && (
              <Text style={styles.noticeText}>
                {t('agreements.fixedTermDurationHint', { days: Math.round((endDate.getTime() - startDate.getTime()) / 86400000) })}
              </Text>
            )}
          </>
        )}

        {agreementType === 'ongoing' && (
          <Text style={styles.noticeText}>{t('agreements.ongoingHint')}</Text>
        )}

        <Text style={styles.sectionTitle}>{t('agreements.multi_sectionTitle')}</Text>
        <Text style={styles.noticeText}>{t('agreements.multi_lockHint')}</Text>

        <View style={styles.modeToggleRow}>
          <Pressable style={[styles.modeChip, splitMode === 'percent' && styles.modeChipActive]} onPress={() => switchSplitMode('percent')}>
            <Text style={[styles.modeChipText, splitMode === 'percent' && styles.modeChipTextActive]}>{t('agreements.multi_percentMode')}</Text>
          </Pressable>
          <Pressable style={[styles.modeChip, splitMode === 'amount' && styles.modeChipActive]} onPress={() => switchSplitMode('amount')}>
            <Text style={[styles.modeChipText, splitMode === 'amount' && styles.modeChipTextActive]}>{t('agreements.multi_amountMode')}</Text>
          </Pressable>
        </View>

        <View style={styles.shareRow}>
          <Text style={styles.shareName} numberOfLines={1}>{t('agreements.multi_youCreator')}</Text>
          <View style={styles.shareInputWrap}>
            <TextInput
              style={styles.shareInput}
              value={shareDisplayValue('creator', creatorShare)}
              onChangeText={onChangeCreatorShare}
              onBlur={stopEditingShare}
              keyboardType="decimal-pad"
            />
            <Text style={styles.sharePercentSign}>{unitSuffix}</Text>
          </View>
          <Pressable onPress={toggleCreatorLock} hitSlop={8} accessibilityRole="button" accessibilityLabel={creatorLocked ? t('agreements.multi_unlockShare') : t('agreements.multi_lockShare')}>
            <Ionicons name={creatorLocked ? 'lock-closed' : 'lock-open-outline'} size={18} color={creatorLocked ? colors.primary : colors.textMuted} />
          </Pressable>
        </View>

        {parties.map((p, i) => {
          const key = partyKey(p);
          const locked = lockedIds.has(key);
          return (
            <View key={key} style={styles.shareRow}>
              <Text style={styles.shareName} numberOfLines={1}>{partyLabel(p, t)}</Text>
              <View style={styles.shareInputWrap}>
                <TextInput
                  style={styles.shareInput}
                  value={shareDisplayValue(key, p.share)}
                  onChangeText={(v) => onChangePartyShare(i, v)}
                  onBlur={stopEditingShare}
                  keyboardType="decimal-pad"
                />
                <Text style={styles.sharePercentSign}>{unitSuffix}</Text>
              </View>
              <Pressable onPress={() => togglePartyLock(key)} hitSlop={8} accessibilityRole="button" accessibilityLabel={locked ? t('agreements.multi_unlockShare') : t('agreements.multi_lockShare')}>
                <Ionicons name={locked ? 'lock-closed' : 'lock-open-outline'} size={18} color={locked ? colors.primary : colors.textMuted} />
              </Pressable>
              <Pressable onPress={() => removeParty(i)} hitSlop={8}>
                <Ionicons name="close-circle" size={20} color={colors.textMuted} />
              </Pressable>
            </View>
          );
        })}

        <Text style={[styles.totalText, Math.abs(total - splitTotal) > 0.5 && styles.totalTextError]}>
          {t('agreements.multi_totalLabel', {
            total: total.toFixed(splitMode === 'percent' ? 1 : 2),
            unit: unitSuffix,
            ofTotal: splitMode === 'amount' ? `/ ${splitTotal.toFixed(2)}${currencySymbol}` : '',
          })}
        </Text>

        <Text style={styles.shareName}>{t('agreements.multi_addPersonLabel')}</Text>
        <View style={styles.searchInputWrap}>
          <Ionicons name="search" size={15} color={colors.textMuted} style={{ marginRight: 4 }} />
          <TextInput
            style={styles.searchInputField}
            value={query}
            onChangeText={onSearch}
            placeholder={t('agreements.multi_searchPlaceholder')}
            placeholderTextColor={colors.textMuted}
            autoCapitalize="none"
            autoCorrect={false}
          />
          {query.length > 0 && (
            <Pressable hitSlop={8} onPress={() => { setQuery(''); setResults([]); }}>
              <Ionicons name="close-circle" size={17} color={colors.textMuted} />
            </Pressable>
          )}
        </View>
        {searching && <ActivityIndicator color={colors.primary} />}
        {results.map((p) => (
          <Pressable key={p.id} style={styles.resultRow} onPress={() => addParty(p)} accessibilityRole="button">
            <Ionicons name="add-circle-outline" size={18} color={colors.primary} />
            <Text style={styles.resultText}>{p.full_name || t('agreements.multi_userFallback')}</Text>
          </Pressable>
        ))}
        {isEmailQuery && (
          <Pressable style={styles.resultRow} onPress={addPartyByEmail} accessibilityRole="button">
            <Ionicons name="mail-outline" size={18} color={colors.primary} />
            <Text style={styles.resultText}>{t('agreements.multi_inviteResultText', { query: query.trim() })}</Text>
          </Pressable>
        )}

        <ErrorText>{error}</ErrorText>

        <Button title={t('agreements.multi_reviewButton')} onPress={goToReview} disabled={parties.length < 2} />
        </>
        )}

        {step === 'review' && (
          <View style={styles.reviewCard}>
            <Text style={styles.reviewTitle}>{title.trim()}</Text>
            {!!description.trim() && <Text style={styles.reviewDesc}>{description.trim()}</Text>}
            {!!amount.trim() && <Text style={styles.reviewAmount}>{t('agreements.multi_reviewTotalAmount', { amount: Number(amount.trim().replace(',', '.')).toFixed(2) })}</Text>}

            <View style={styles.reviewPartyRow}>
              <Text style={styles.reviewPartyName}>{t('agreements.reviewTypeLabel')}</Text>
              <Text style={styles.reviewPartyShare}>
                {agreementType === 'one_off' ? t('agreements.type_one_off') : agreementType === 'ongoing' ? t('agreements.type_ongoing') : t('agreements.type_fixed_term')}
              </Text>
            </View>
            {agreementType === 'one_off' && expiryDate && (
              <View style={styles.reviewPartyRow}>
                <Text style={styles.reviewPartyName}>{expiryLabel}</Text>
                <Text style={styles.reviewPartyShare}>{expiryDate.toLocaleDateString('el-GR')}</Text>
              </View>
            )}
            {agreementType === 'fixed_term' && startDate && endDate && (
              <View style={styles.reviewPartyRow}>
                <Text style={styles.reviewPartyName}>{t('agreements.reviewPeriodLabel')}</Text>
                <Text style={styles.reviewPartyShare}>{startDate.toLocaleDateString('el-GR')} – {endDate.toLocaleDateString('el-GR')}</Text>
              </View>
            )}

            <View style={styles.reviewDivider} />
            <Text style={styles.sectionTitle}>{t('agreements.multi_sectionTitle')}</Text>
            <View style={styles.reviewPartyRow}>
              <Text style={styles.reviewPartyName}>{t('agreements.multi_youCreator')}</Text>
              <Text style={styles.reviewPartyShare}>{creatorShare}{unitSuffix}</Text>
            </View>
            {parties.map((p) => (
              <View key={partyKey(p)} style={styles.reviewPartyRow}>
                <Text style={styles.reviewPartyName} numberOfLines={1}>{partyLabel(p, t)}</Text>
                <Text style={styles.reviewPartyShare}>{p.share}{unitSuffix}</Text>
              </View>
            ))}

            <View style={styles.reviewNotice}>
              <Ionicons name="information-circle-outline" size={14} color={colors.textMuted} />
              <Text style={styles.noticeText}>{t('agreements.multi_reviewNotice')}</Text>
            </View>

            <ErrorText>{error}</ErrorText>

            <View style={styles.reviewActions}>
              <View style={styles.reviewActionBtn}>
                <Button title={t('agreements.multi_back')} variant="secondary" onPress={() => setStep('form')} />
              </View>
              <View style={styles.reviewActionBtn}>
                <Button title={t('agreements.multi_saveAsDraft')} onPress={onSubmit} loading={busy} />
              </View>
            </View>
          </View>
        )}
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    content: {
      // Σε μεγάλες οθόνες η φόρμα μένει σε αναγνώσιμο πλάτος αντί να
      // τεντώνεται· σε κινητό το maxWidth δεν ενεργοποιείται ποτέ.
      width: '100%',
      maxWidth: contentMaxWidth.form,
      alignSelf: 'center',
      paddingHorizontal: spacing.lg,
      gap: spacing.md,
    },
    notice: {
      flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
      backgroundColor: colors.surfaceAlt, borderRadius: radius.md, padding: spacing.sm,
    },
    noticeText: { color: colors.textMuted, fontSize: fontSize.xs, flex: 1 },
    sectionTitle: { color: colors.text, fontSize: fontSize.md, fontWeight: '700', marginTop: spacing.sm },
    fieldLabel: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '500',
    },
    templateSection: { gap: spacing.xs },
    templateSectionLabel: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '500',
    },
    templateChipRow: {
      flexDirection: 'row',
      gap: spacing.sm,
      paddingVertical: spacing.xs,
    },
    templateChip: {
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderRadius: radius.full,
      backgroundColor: colors.surfaceAlt,
      borderWidth: 1,
      borderColor: colors.border,
      maxWidth: 220,
    },
    templateChipActive: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    templateChipText: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    templateChipTextActive: {
      color: colors.textOnPrimary,
    },
    typeCards: {
      flexDirection: 'row',
      gap: spacing.xs,
    },
    typeCard: {
      flex: 1,
      alignItems: 'center',
      gap: 4,
      padding: spacing.sm,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    typeCardSelected: {
      borderColor: colors.primary,
      backgroundColor: `${colors.primary}14`,
    },
    typeCardLabel: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    typeCardLabelSelected: {
      color: colors.primary,
    },
    typeCardSub: {
      color: colors.textMuted,
      fontSize: 10,
      textAlign: 'center',
    },
    labelChips: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: spacing.xs,
      marginTop: spacing.xs,
    },
    labelChip: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.full,
      paddingVertical: 5,
      paddingHorizontal: spacing.sm,
    },
    labelChipSelected: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    labelChipText: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    labelChipTextSelected: { color: colors.textOnPrimary },
    modeToggleRow: { flexDirection: 'row', gap: spacing.xs, marginBottom: spacing.xs },
    modeChip: {
      flex: 1,
      alignItems: 'center',
      paddingVertical: spacing.xs,
      borderRadius: radius.full,
      backgroundColor: colors.surfaceAlt,
      borderWidth: 1,
      borderColor: colors.border,
    },
    modeChipActive: { backgroundColor: colors.primary, borderColor: colors.primary },
    modeChipText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '700' },
    modeChipTextActive: { color: '#fff' },
    shareRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    shareName: { flexShrink: 1, flexGrow: 1, flexBasis: 0, maxWidth: '42%', color: colors.text, fontSize: fontSize.md, fontWeight: '600' },
    shareInputWrap: { flexDirection: 'row', alignItems: 'center', gap: 4, flexShrink: 0 },
    shareInput: {
      width: 60, height: 44, borderRadius: radius.md, borderWidth: 1, borderColor: colors.border,
      backgroundColor: colors.surfaceAlt, color: colors.text, textAlign: 'center', fontSize: fontSize.md, fontWeight: '700',
    },
    amountInputLarge: {
      fontSize: fontSize.lg,
      fontWeight: '700',
    },
    currencyTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      alignSelf: 'flex-start',
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      paddingVertical: 7,
      paddingHorizontal: spacing.sm,
      backgroundColor: colors.surfaceAlt,
    },
    currencyTriggerSymbol: {
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '800',
      minWidth: 14,
      textAlign: 'center',
    },
    currencyTriggerLabel: { color: colors.textMuted, fontSize: fontSize.sm },
    currencyDropdown: {
      marginTop: 4,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      backgroundColor: colors.surface,
      overflow: 'hidden',
    },
    currencyOption: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingVertical: 10,
      paddingHorizontal: spacing.md,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
    },
    currencyOptionSelected: { backgroundColor: `${colors.primary}12` },
    currencyOptionSymbol: { color: colors.textMuted, fontSize: fontSize.md, fontWeight: '700', width: 20, textAlign: 'center' },
    currencyOptionSymbolSelected: { color: colors.primary },
    currencyOptionLabel: { color: colors.text, fontSize: fontSize.sm, flex: 1 },
    currencyOptionLabelSelected: { color: colors.primary, fontWeight: '700' },
    currencyOptionCode: { color: colors.textMuted, fontSize: fontSize.xs },
    sharePercentSign: { color: colors.textMuted, fontSize: fontSize.sm },
    totalText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    totalTextError: { color: colors.danger },
    searchInputWrap: {
      flexDirection: 'row',
      alignItems: 'center',
      height: 48,
      backgroundColor: colors.surfaceAlt,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      paddingLeft: spacing.md,
      paddingRight: spacing.sm,
    },
    searchInputField: { flex: 1, color: colors.text, fontSize: fontSize.md },
    reviewCard: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.lg,
      gap: spacing.xs,
    },
    reviewTitle: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    reviewDesc: { color: colors.textMuted, fontSize: fontSize.sm },
    reviewAmount: { color: colors.text, fontSize: fontSize.md, fontWeight: '600', marginTop: spacing.xs },
    reviewDivider: { height: 1, backgroundColor: colors.border, marginVertical: spacing.sm },
    reviewPartyRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4 },
    reviewPartyName: { flex: 1, color: colors.text, fontSize: fontSize.md },
    reviewPartyShare: { color: colors.textMuted, fontSize: fontSize.md, fontWeight: '700' },
    reviewNotice: {
      flexDirection: 'row', alignItems: 'flex-start', gap: spacing.xs,
      backgroundColor: colors.surfaceAlt, borderRadius: radius.md, padding: spacing.sm, marginTop: spacing.md,
    },
    reviewActions: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.md },
    reviewActionBtn: { flex: 1 },
    resultRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.xs },
    resultText: { color: colors.text, fontSize: fontSize.md },
    error: { color: colors.danger, fontSize: fontSize.sm },
  });
}
